import type { Key } from "@react-types/shared";
import { EMPTY_ALGOLIA_QUERY } from "core/consts";
import type {
  AlgoliaAnalyticsName,
  AlgoliaAutocompleteOption,
} from "core/types";
import { useAlgoliaSearchClient } from "dsl/hooks/useAlgoliaSearchClient";
import { debounce } from "lodash";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Autocomplete, AutocompleteProps } from "../autocomplete";

/**
 * Drop-in replacement for `algoliasearch().search` used by {@link AlgoliaAutocomplete}
 * in Storybook/tests so requests never hit the network.
 */
export type AlgoliaAutocompleteMockSearchClient<THit = unknown> = {
  search: (
    queries: {
      indexName: string;
      params?: {
        filters?: string;
        hitsPerPage?: number;
        query?: string;
      };
    }[],
  ) => Promise<{
    results: Array<{ hits?: THit[] }>;
  }>;
};

export type AlgoliaAutocompleteProps<
  THit,
  TItem extends AlgoliaAutocompleteOption = AlgoliaAutocompleteOption,
> = Omit<
  AutocompleteProps<TItem>,
  | "children"
  | "defaultFilter"
  | "items"
  | "onSelectionChange"
  | "renderItem"
  | "selectedKey"
> & {
  algoliaAnalyticsName: AlgoliaAnalyticsName;
  debounceMs?: number;
  filters?: string;
  hitsPerPage?: number;
  indexName: string;
  mapHit: (hit: THit) => TItem;
  minCharacterForRefinement?: number;
  /** When set, used instead of the real Algolia client (Storybook / tests). */
  mockSearchClient?: AlgoliaAutocompleteMockSearchClient<THit>;
  onSelectedOptionChange?: (option: TItem | null) => void;
  renderItem?: AutocompleteProps<TItem>["renderItem"];
  selectedOption?: TItem | null;
  shouldSearchOnInitialRender?: boolean;
  withIndexEnv?: boolean;
};

const defaultRenderItem = <TItem extends AlgoliaAutocompleteOption>(
  item: TItem,
): { node: ReactNode; textValue: string } => ({
  node: item.label,
  textValue: item.label,
});

/**
 * Algolia-backed async filtering for the design-system `Autocomplete` (HeroUI v2).
 *
 * HeroUI v3 uses a different composition API (`Autocomplete.Filter`, `useAsyncList`, etc.).
 * Until the app upgrades `@heroui/react`, this component matches that behavior using
 * controlled `items`, debounced `onInputChange`, and `defaultFilter` so results are not
 * re-filtered client-side after Algolia returns hits.
 *
 * **Controlled search text:** pass `inputValue` and `onInputChange` together (HeroUI /
 * React Aria). Selection stays in sync via `selectedOption` / `onSelectedOptionChange`
 * (maps to `selectedKey`).
 *
 * **React Hook Form:** use {@link ControlledAlgoliaAutocomplete} from `ds/ui` with a
 * `name` prop inside `FormProvider`.
 *
 * **Mocked Algolia:** pass `mockSearchClient` (see Storybook) to avoid network.
 */
export function AlgoliaAutocomplete<
  THit,
  TItem extends AlgoliaAutocompleteOption = AlgoliaAutocompleteOption,
>({
  algoliaAnalyticsName,
  debounceMs = 100,
  filters,
  hitsPerPage = 50,
  indexName,
  mapHit,
  minCharacterForRefinement = 0,
  mockSearchClient,
  onSelectedOptionChange,
  renderItem: renderItemFromProps,
  selectedOption,
  shouldSearchOnInitialRender = false,
  withIndexEnv,
  ...autocompleteProps
}: AlgoliaAutocompleteProps<THit, TItem>) {
  const {
    onClear: onClearFromAutocomplete,
    onInputChange: onInputChangeFromProps,
    ...restAutocompleteProps
  } = autocompleteProps;
  const {
    analyticsTags,
    indexNameWithEnv,
    searchClient: liveSearchClient,
  } = useAlgoliaSearchClient({
    algoliaAnalyticsName,
    indexName,
    indexWithEnv: withIndexEnv,
  });

  const [items, setItems] = useState<TItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const runSearch = useCallback(
    async (query: string, signal: AbortSignal) => {
      const meetsMinLength = query.length >= minCharacterForRefinement;
      const isInitialEmptyQuery =
        query === EMPTY_ALGOLIA_QUERY && shouldSearchOnInitialRender;

      if (!meetsMinLength && !isInitialEmptyQuery) {
        setItems([]);
        return;
      }

      setIsLoading(true);
      try {
        const queryParam = query || EMPTY_ALGOLIA_QUERY;
        const response = mockSearchClient
          ? await mockSearchClient.search([
              {
                indexName: indexNameWithEnv,
                params: { filters, hitsPerPage, query: queryParam },
              },
            ])
          : await liveSearchClient.search([
              {
                indexName: indexNameWithEnv,
                params: {
                  analyticsTags,
                  clickAnalytics: true,
                  filters: filters ?? "",
                  hitsPerPage,
                  query: queryParam,
                },
              },
            ]);

        if (signal.aborted) {
          return;
        }

        const first = response.results[0];
        const rawHits = first && "hits" in first ? (first.hits as THit[]) : [];
        setItems(rawHits.map(mapHit));
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [
      analyticsTags,
      filters,
      hitsPerPage,
      indexNameWithEnv,
      liveSearchClient,
      mapHit,
      mockSearchClient,
      minCharacterForRefinement,
      shouldSearchOnInitialRender,
    ],
  );

  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  const debouncedRunSearch = useMemo(
    () =>
      debounce((query: string, signal: AbortSignal) => {
        void runSearchRef.current(query, signal);
      }, debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    return () => {
      debouncedRunSearch.cancel();
      abortRef.current?.abort();
    };
  }, [debouncedRunSearch]);

  useEffect(() => {
    if (!shouldSearchOnInitialRender) {
      return;
    }

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    void runSearch(EMPTY_ALGOLIA_QUERY, ac.signal);
  }, [runSearch, shouldSearchOnInitialRender]);

  const itemsForList = useMemo(() => {
    if (
      selectedOption &&
      !items.some((i) => String(i.value) === String(selectedOption.value))
    ) {
      return [selectedOption, ...items];
    }
    return items;
  }, [items, selectedOption]);

  const handleInputChange = (value: string) => {
    onInputChangeFromProps?.(value);

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    const meetsMinLength = value.length >= minCharacterForRefinement;
    const treatAsInitialBrowse =
      value === "" &&
      shouldSearchOnInitialRender &&
      minCharacterForRefinement === 0;

    if (!meetsMinLength && !treatAsInitialBrowse) {
      debouncedRunSearch.cancel();
      setItems([]);
      setIsLoading(false);
      return;
    }

    const query = value || EMPTY_ALGOLIA_QUERY;

    if (mockSearchClient) {
      debouncedRunSearch.cancel();
      void runSearchRef.current(query, ac.signal);
      return;
    }

    debouncedRunSearch(query, ac.signal);
  };

  const handleSelectionChange = (key: Key | null) => {
    if (key == null) {
      onSelectedOptionChange?.(null);
      return;
    }
    const keyStr = String(key);
    const found = itemsForList.find(
      (i) => String(i.id) === keyStr || String(i.value) === keyStr,
    );
    onSelectedOptionChange?.(found ?? null);
    // After a pick, drop async hits so the list is empty; combined with
    // `menuTrigger="input"` this stops the menu from reopening when the input
    // keeps focus after selecting — the "dropdown does not collapse after
    // selection following a validation error" bug (POW-90), mirroring the
    // MapboxAddressAutocomplete fix (DEV-20544). The committed option still
    // resolves via `itemsForList`, which prepends `selectedOption`.
    if (found != null) {
      debouncedRunSearch.cancel();
      abortRef.current?.abort();
      setItems([]);
      setIsLoading(false);
    }
  };

  const renderItem = renderItemFromProps ?? defaultRenderItem;

  return (
    <Autocomplete<TItem>
      {...restAutocompleteProps}
      allowsEmptyCollection
      defaultFilter={() => true}
      isLoading={isLoading}
      items={itemsForList}
      menuTrigger="input"
      renderItem={renderItem}
      selectedKey={selectedOption?.value ?? null}
      onClear={() => {
        onClearFromAutocomplete?.();
        onSelectedOptionChange?.(null);
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    />
  );
}

AlgoliaAutocomplete.displayName = "Recare.AlgoliaAutocomplete";
